/*
  nytstnd-grouped-card
  --------------------
  Collection-card behavior for "color groups" made of SEPARATE products
  (NYTSTND stores each color as its own product, not as variants).

  A primary product carries metafield custom.color_variants (list of the
  sibling color products). The card renders one swatch per sibling. Clicking a
  swatch swaps the card's image, price and title IN PLACE and repoints the
  card's links at that color's own product page — no page reload. Clicking
  anywhere else on the card navigates to the currently selected color.

  Mirrors the stock `loess-product-card` swatch interaction, extended to swap
  price/title/URL across separate products.
*/
if (!customElements.get('nytstnd-grouped-card')) {
  customElements.define(
    'nytstnd-grouped-card',
    class extends HTMLElement {
      connectedCallback() {
        if (this._wired) return;
        this.swatches = Array.from(this.querySelectorAll('.card-swatches__button'));
        if (!this.swatches.length) return;

        this.images = Array.from(this.querySelectorAll('.card__primary-image'));
        this.priceBlocks = Array.from(this.querySelectorAll('[data-grouped-price]'));
        this.titleEls = Array.from(this.querySelectorAll('[data-grouped-title]'));
        this.productLinks = Array.from(this.querySelectorAll('[data-grouped-link]'));
        this.secondaryImage = this.querySelector('.card__secondary-image');

        this.swatches.forEach((swatch) => {
          swatch.addEventListener('mouseenter', () => this.preload(swatch));
          swatch.addEventListener('click', (event) => {
            event.preventDefault();
            this.select(swatch);
          });
        });

        // Rotate-on-hover: auto-cycle the selected color's photos
        this.rotateEnabled = this.hasAttribute('data-rotate-on-hover');
        if (this.rotateEnabled) {
          this.rotateInterval = parseInt(this.dataset.rotateInterval, 10) || 700;
          this.rotatingImages = Array.from(this.querySelectorAll('.card__rotating-image'));
          this.progressBar = this.querySelector('.card__rotation-progress');
          const activeSwatch =
            this.querySelector('.card-swatches__button--active') || this.swatches[0];
          this.activeColorId = activeSwatch ? activeSwatch.dataset.colorId : null;
          this.addEventListener('mouseenter', () => {
            this._hovering = true;
            this.startRotation();
          });
          this.addEventListener('mouseleave', () => {
            this._hovering = false;
            this.stopRotation();
          });
        }

        this._wired = true;
      }

      imageFor(swatch) {
        return this.images.find((img) => img.dataset.mediaId === swatch.dataset.mediaId);
      }

      // Frames for a color, in order: that color's primary image followed by its extras
      framesForColor(colorId) {
        const primary = this.images.find((img) => img.dataset.colorId === colorId);
        const extras = this.rotatingImages
          .filter((img) => img.dataset.colorId === colorId)
          .sort((a, b) => Number(a.dataset.rotateIndex) - Number(b.dataset.rotateIndex));
        return [primary, ...extras].filter(Boolean);
      }

      startRotation() {
        if (!this.rotateEnabled) return;
        const frames = this.framesForColor(this.activeColorId);
        if (frames.length <= 1) return;
        // Reveal this color's extra frames so the browser loads them (they begin hidden)
        frames.forEach((img) => {
          if (img.classList.contains('card__rotating-image')) {
            img.removeAttribute('hidden');
            img.setAttribute('loading', 'eager');
          }
        });
        this.buildProgress(frames.length);
        this.updateProgress(0);
        let i = 0;
        clearInterval(this._timer);
        this._timer = setInterval(() => {
          i = (i + 1) % frames.length;
          this.showFrame(frames, i);
          this.updateProgress(i);
        }, this.rotateInterval);
      }

      showFrame(frames, i) {
        // Index 0 is the primary image (always underneath); fade in the current extra
        this.rotatingImages.forEach((img) => img.classList.remove('card__rotating-image--current'));
        const current = frames[i];
        if (current && current.classList.contains('card__rotating-image')) {
          current.classList.add('card__rotating-image--current');
        }
      }

      // Story-style segmented progress bar — one segment per frame of the active color
      buildProgress(count) {
        if (!this.progressBar) return;
        this.progressBar.innerHTML = '';
        this.progressSegments = [];
        for (let n = 0; n < count; n += 1) {
          const seg = document.createElement('span');
          seg.className = 'card__rotation-progress-segment';
          const fill = document.createElement('span');
          fill.className = 'card__rotation-progress-fill';
          seg.appendChild(fill);
          this.progressBar.appendChild(seg);
          this.progressSegments.push(seg);
        }
        this.progressBar.classList.add('is-visible');
      }

      updateProgress(i) {
        if (!this.progressSegments) return;
        this.progressSegments.forEach((seg, idx) => {
          const fill = seg.firstChild;
          if (idx < i) {
            // already shown — keep filled
            fill.style.transition = 'none';
            fill.style.width = '100%';
          } else if (idx > i) {
            // not yet reached — empty
            fill.style.transition = 'none';
            fill.style.width = '0%';
          }
        });
        // Current segment: restart the fill animation from 0 over one interval
        const active = this.progressSegments[i];
        if (active) {
          const fill = active.firstChild;
          fill.style.transition = 'none';
          fill.style.width = '0%';
          void fill.offsetWidth; // force reflow so the transition restarts
          fill.style.transition = 'width ' + this.rotateInterval + 'ms linear';
          fill.style.width = '100%';
        }
      }

      stopRotation() {
        clearInterval(this._timer);
        this._timer = null;
        if (this.rotatingImages) {
          this.rotatingImages.forEach((img) => {
            img.classList.remove('card__rotating-image--current');
            img.setAttribute('hidden', 'true');
          });
        }
        if (this.progressBar) {
          this.progressBar.innerHTML = '';
          this.progressBar.classList.remove('is-visible');
          this.progressSegments = null;
        }
      }

      preload(swatch) {
        const img = this.imageFor(swatch);
        if (img) img.setAttribute('loading', 'eager');
      }

      select(swatch) {
        const mediaId = swatch.dataset.mediaId;

        // Switch the rotation set to the newly selected color
        if (this.rotateEnabled) {
          this.stopRotation();
          this.activeColorId = swatch.dataset.colorId;
          if (this._hovering) this.startRotation();
        }

        // Image
        const img = this.imageFor(swatch);
        if (img) {
          this.images.forEach((other) => other.setAttribute('hidden', 'true'));
          img.removeAttribute('hidden');
        }

        // Price (pre-rendered per color so money formatting / sale styling stays correct)
        this.priceBlocks.forEach((block) => {
          if (block.dataset.mediaId === mediaId) {
            block.removeAttribute('hidden');
          } else {
            block.setAttribute('hidden', 'true');
          }
        });

        // Title (variant metafield may contain HTML such as <br>, so render as markup).
        // Source is the store's own custom.variant metafield — same trust level as the
        // Liquid `{{ ... }}` that renders it on initial load.
        if (swatch.dataset.title) {
          this.titleEls.forEach((el) => {
            el.innerHTML = swatch.dataset.title;
          });
        }

        // Hover (second) image — swap the single secondary image's source to the selected color
        if (this.secondaryImage && swatch.dataset.hoverSrc) {
          this.secondaryImage.src = swatch.dataset.hoverSrc;
          this.secondaryImage.srcset = swatch.dataset.hoverSrc;
        }

        // Repoint every card link at the selected color's product page
        if (swatch.dataset.productUrl) {
          this.productLinks.forEach((link) => {
            link.setAttribute('href', swatch.dataset.productUrl);
          });
        }

        // Active swatch state
        this.swatches.forEach((other) => other.classList.remove('card-swatches__button--active'));
        swatch.classList.add('card-swatches__button--active');
      }
    }
  );
}
